import { Dispatch, SetStateAction, useId, useRef, useState } from "react";
import { FoodsDeleteButton, FoodsTextInput, Typography } from "@foodpilot/foods";
import { iconMapping } from "@foodpilot/foods/Icons";
import { Box, Button, Stack, SvgIcon, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { AddItemField, FormItemRow } from "src/components/EntityForm";
import { ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import { PackagingSearchModalV2 } from "src/pages/Product/v2/ProductFormV2/PackagingSearch/PackagingSearchModalV2";
import type { FieldError } from "../validation";
import { getPackagingLineScoreKey } from "../sectionScores";
import { formatNumber } from "src/utils/formatting";
import { LineScoreValue } from "../Layout/LineScoreValue";

type Step6PackagingProps = {
    product: ProductFormType;
    setProduct: Dispatch<SetStateAction<ProductFormType>>;
    errors?: FieldError[];
    lineScores?: Record<string, string | null>;
    scoreUnit?: string;
};

type ProductPackaging = ProductFormType["productPackagings"][number];

const isDirectElementReference = (productPackaging: ProductPackaging): boolean =>
    productPackaging.packagingReference?.kind === "element";

export const Step6Packaging = (props: Step6PackagingProps) => {
    const { product, setProduct, errors = [], lineScores = {}, scoreUnit = "Pts" } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const [isPackagingModalOpen, setPackagingModalOpen] = useState(false);
    const packagingModalTargetIndexRef = useRef<number | null>(null);

    const setProductPackagings = (productPackagings: ProductFormType["productPackagings"]) => {
        setProduct((currentProduct) => ({
            ...currentProduct,
            productPackagings,
        }));
    };

    const updatePackagingAtIndex = (index: number, patch: Partial<ProductFormType["productPackagings"][number]>) => {
        setProductPackagings(
            product.productPackagings.map((packaging, currentIndex) => {
                if (currentIndex !== index) {
                    return packaging;
                }

                return {
                    ...packaging,
                    ...patch,
                };
            }),
        );
    };

    const removePackagingAtIndex = (index: number) => {
        setProductPackagings(product.productPackagings.filter((_, currentIndex) => currentIndex !== index));
    };

    const openPackagingModal = (index: number | null) => {
        packagingModalTargetIndexRef.current = index;
        setPackagingModalOpen(true);
    };

    const selectPackagingFromModal = (selectedPackaging: ProductFormType["productPackagings"][number]) => {
        if (packagingModalTargetIndexRef.current === null) {
            setProductPackagings([...product.productPackagings, selectedPackaging]);

            return;
        }

        setProductPackagings(
            product.productPackagings.map((packaging, currentIndex) =>
                currentIndex === packagingModalTargetIndexRef.current ? selectedPackaging : packaging,
            ),
        );
    };

    const packagingKey = (productPackaging: ProductFormType["productPackagings"][number], index: number) =>
        productPackaging["@id"] ??
        productPackaging.packaging?.["@id"] ??
        productPackaging.packagingReference?.["@id"] ??
        productPackaging.packagingReference?.compositeId ??
        `packaging-${index}`;

    const packagingName = (productPackaging: ProductFormType["productPackagings"][number]) =>
        productPackaging.packaging?.name ?? productPackaging.packagingReference?.name ?? "";

    return (
        <Box
            sx={{
                padding: theme.spacing(3),
            }}
        >
            <PackagingSearchModalV2
                isOpen={isPackagingModalOpen}
                setOpen={setPackagingModalOpen}
                onSelectPackaging={selectPackagingFromModal}
            />

            {product.productPackagings.length === 0 ?
                <AddItemField
                    description={t("product.v2.packaging.empty_state", "Add a first packaging to your product.")}
                    actionLabel={t("product.v2.packaging.add_a_packaging", "Add a packaging")}
                    onAction={() => openPackagingModal(null)}
                />
            :   <Stack sx={{ gap: theme.spacing(3) }}>
                    <Stack sx={{ gap: theme.spacing(2) }}>
                        {product.productPackagings.map((productPackaging, index) => (
                            <PackagingLine
                                key={packagingKey(productPackaging, index)}
                                productPackaging={productPackaging}
                                packagingName={packagingName(productPackaging)}
                                lineScore={lineScores[getPackagingLineScoreKey(productPackaging) ?? ""] ?? null}
                                scoreUnit={scoreUnit}
                                error={errors.find((error) => error.field.startsWith(`productPackagings.${index}.`))}
                                onOpenModal={() => openPackagingModal(index)}
                                onRemove={() => removePackagingAtIndex(index)}
                                onChangeValue={(newValue) => {
                                    updatePackagingAtIndex(
                                        index,
                                        isDirectElementReference(productPackaging) ?
                                            { usedWeight: newValue === "" ? null : Number(newValue), quantity: null }
                                        :   { quantity: newValue === "" ? null : Number(newValue), usedWeight: null },
                                    );
                                }}
                            />
                        ))}
                    </Stack>

                    <AddItemField
                        actionLabel={t("product.v2.packaging.add_a_packaging", "Add a packaging")}
                        onAction={() => openPackagingModal(null)}
                    />
                </Stack>
            }
        </Box>
    );
};

type PackagingLineProps = {
    productPackaging: ProductPackaging;
    packagingName: string;
    lineScore: string | null;
    scoreUnit: string;
    error?: FieldError;
    onOpenModal: () => void;
    onRemove: () => void;
    onChangeValue: (value: string) => void;
};

const PackagingLine = (props: PackagingLineProps) => {
    const { productPackaging, packagingName, lineScore, scoreUnit, error, onOpenModal, onRemove, onChangeValue } =
        props;
    const theme = useTheme();
    const isCompactLayout = useMediaQuery(theme.breakpoints.down("sm"));
    const { t, i18n } = useTranslation();
    const errorId = useId();
    const isElement = isDirectElementReference(productPackaging);
    const isTenantElement = productPackaging.packaging?.["@type"] === "PackagingElement";
    const formattedTenantWeight =
        (
            isTenantElement &&
            productPackaging.packaging?.weight !== null &&
            productPackaging.packaging?.weight !== undefined
        ) ?
            formatNumber(productPackaging.packaging.weight, i18n.resolvedLanguage, { maximumFractionDigits: 3 })
        :   null;
    const expectedValueLabel = isElement ? t("glossary.weight", "Weight") : t("packaging_composite.amount", "Quantity");
    const packagingTypeLabel =
        isElement ? t("product.v2.packaging.material", "Material") : t("product.v2.packaging.packaging", "Packaging");
    const value = isElement ? productPackaging.usedWeight : productPackaging.quantity;
    const displayedPackagingName =
        formattedTenantWeight === null ? packagingName : (
            `${packagingName} (${formattedTenantWeight} ${t("weight.grams", "g")})`
        );
    return (
        <FormItemRow testId="packaging-row">
            <Stack
                direction={isCompactLayout ? "column" : "row"}
                alignItems={isCompactLayout ? "stretch" : "flex-start"}
                sx={{
                    flex: 1,
                    gap: theme.spacing(1),
                    minWidth: 0,
                    width: isCompactLayout ? "100%" : undefined,
                }}
            >
                <Stack sx={{ flex: isCompactLayout ? undefined : 1, gap: theme.spacing(1), minWidth: 0 }}>
                    <Typography variant="body-semibold" sx={{ color: theme.custom.grey[2800] }}>
                        {packagingTypeLabel}
                    </Typography>
                    <Button
                        variant="select"
                        onClick={onOpenModal}
                        sx={{
                            color: theme.custom.grey[2500],
                            gap: theme.spacing(1.5),
                            justifyContent: "space-between",
                            minWidth: 0,
                            paddingLeft: theme.spacing(2.5),
                            paddingRight: theme.spacing(1.5),
                            width: "100%",
                        }}
                    >
                        <Typography variant="body-regular" noWrap sx={{ color: "inherit", minWidth: 0 }}>
                            {displayedPackagingName}
                        </Typography>
                        <SvgIcon
                            component={iconMapping.arrowDownShort}
                            inheritViewBox
                            sx={{ flexShrink: 0, height: theme.spacing(2.5), width: theme.spacing(2.5) }}
                        />
                    </Button>
                </Stack>

                <Stack
                    sx={{
                        flexShrink: 0,
                        gap: theme.spacing(1),
                        width: isCompactLayout ? "100%" : "auto",
                    }}
                >
                    <Typography variant="body-semibold" sx={{ color: theme.custom.grey[2800] }}>
                        {expectedValueLabel}*
                    </Typography>
                    <FoodsTextInput
                        value={value?.toString() ?? ""}
                        unit={isElement ? t("weight.g", "g") : undefined}
                        error={error !== undefined}
                        onChange={onChangeValue}
                        ExtraInputProps={{
                            inputProps: {
                                "aria-label": expectedValueLabel,
                                "aria-required": true,
                                "aria-invalid": error !== undefined,
                                "aria-describedby": error !== undefined ? errorId : undefined,
                                inputMode: "decimal",
                                min: 0,
                            },
                            type: "number",
                            sx: { width: isCompactLayout ? "100%" : theme.custom.formField.compact.width },
                        }}
                    />
                    {error !== undefined && (
                        <Typography id={errorId} variant="small-regular" sx={{ color: theme.palette.error.main }}>
                            {error.message ??
                                t(error.messageKey, isElement ? "Weight is required" : "Quantity is required")}
                        </Typography>
                    )}
                </Stack>
            </Stack>

            <Box
                sx={{
                    display: "flex",
                    flexShrink: 0,
                    justifyContent: "flex-end",
                    minWidth: theme.custom.scoreValue.inline.width,
                    whiteSpace: "nowrap",
                    width: "max-content",
                }}
            >
                <LineScoreValue value={lineScore} unit={scoreUnit} />
            </Box>

            <Box sx={{ flexShrink: 0 }}>
                <FoodsDeleteButton ariaLabel={t("product.packaging.remove", "Remove packaging")} onClick={onRemove} />
            </Box>
        </FormItemRow>
    );
};
